/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { useSummaryPageConfig } from '@jira/router-resources__service-desk-project-summary/src/useSummaryPageConfig.tsx';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { useNewExperienceFeatureEnabled } from '@jira/servicedesk__project-summary/src/useNewExperienceFeatureEnabled.tsx';
import { useSummaryPageConfigAndByokData } from '@jira/servicedesk__project-summary/src/useSummaryPageConfigAndByokData.tsx';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { JSMProjectSummaryHorizontalNav } from './ui/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from './common/constants.tsx';

const LazyJSMProjectSummaryHorizontalNav = lazyForPaint<typeof JSMProjectSummaryHorizontalNav>(
	() =>
		import(/* webpackChunkName: "jira-project-summary-horizontal-nav" */ './ui/index.tsx').then(
			({ JSMProjectSummaryHorizontalNav }) => JSMProjectSummaryHorizontalNav,
		),
	{
		includeInShellOnlySsrBundle: true,
		entryFsSsrLiftImportToModule: true,
	},
);

const AsyncJSMProjectSummaryHorizontalNavContent = (): React.JSX.Element => (
	<Placeholder name="project-summary-horizontal-nav" fallback={null}>
		<JSErrorBoundary
			id="asyncHorizontalProjectSummaryNav"
			packageName={PACKAGE_NAME}
			fallback="flag"
			teamName={TEAM_NAME}
		>
			<LazyJSMProjectSummaryHorizontalNav />
		</JSErrorBoundary>
	</Placeholder>
);

const JSMProjectSummaryHorizontalNavWithConfig = () => {
	const { loading, isReportingEnabled, isByokCustomer } = useSummaryPageConfigAndByokData();

	if (loading) {
		return <UFOLoadHold name="byok-or-reporting-config-loading-horizontal-nav" hold={loading} />;
	}

	if (isByokCustomer || !isReportingEnabled) {
		return null;
	}

	return <AsyncJSMProjectSummaryHorizontalNavContent />;
};

const ProjectSummaryHorizontalNav = () => {
	const { data: config, loading } = useSummaryPageConfig();
	const isNewExperienceFeatureEnabled = useNewExperienceFeatureEnabled();

	if (loading) {
		return <UFOLoadHold name="reporting-config-loading-horizontal-nav" hold={loading} />;
	}

	const showHorizontalNav = fg('jsm_project_summary_toggle_experience')
		? config?.isReportingEnabled && isNewExperienceFeatureEnabled
		: config?.isReportingEnabled;

	return showHorizontalNav ? <AsyncJSMProjectSummaryHorizontalNavContent /> : null;
};

const ProjectSummaryHorizontalNavWithByok = () => {
	if (fg('jsm_project_summary_fe_ga_issues_fixes')) {
		return <JSMProjectSummaryHorizontalNavWithConfig />;
	}

	return <AsyncJSMProjectSummaryHorizontalNavContent />;
};

export const AsyncJSMProjectSummaryHorizontalNav: React.FC<React.RefAttributes<unknown>> =
	componentWithCondition(
		() => fg('jsm_reporting_enable_byok_checks_summary_page'),
		ProjectSummaryHorizontalNav,
		ProjectSummaryHorizontalNavWithByok,
	);
